import React, {useCallback, useEffect, useState} from 'react';
import {View, ScrollView, StyleSheet, RefreshControl, Alert, Image, TouchableOpacity, Linking} from 'react-native';
import {
  Text,
  Card,
  Button,
  Appbar,
  Divider,
  ActivityIndicator,
  Dialog,
  Portal,
  IconButton,
} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useNavigation, useRoute} from '@react-navigation/native';
import {useCustomerStore} from '../../store/useCustomerStore';
import {useMeasurementStore} from '../../store/useMeasurementStore';
import {colors, spacing} from '../../theme';

const InfoRow = ({label, value}) => (
  <View style={styles.infoRow}>
    <Text variant="bodySmall" style={styles.infoLabel}>{label}</Text>
    <Text variant="bodyMedium" style={styles.infoValue}>{value}</Text>
  </View>
);

const CustomerDetailScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const route = useRoute();
  const {customerId} = route.params;

  const {currentCustomer, isLoading, fetchCustomerDetail, deleteCustomer, clearCurrentCustomer,
    customerStats, fetchCustomerStats, clearCustomerStats} = useCustomerStore();
  const {
    customerProfiles, bodyProfile,
    fetchCustomerProfiles, fetchBodyProfile,
    clearCustomerProfiles, clearBodyProfile,
    deleteProfile,
  } = useMeasurementStore();

  const [refreshing, setRefreshing] = useState(false);
  const [deleteVisible, setDeleteVisible] = useState(false);

  useEffect(() => {
    fetchCustomerDetail(customerId);
    fetchCustomerProfiles(customerId);
    fetchBodyProfile(customerId);
    fetchCustomerStats(customerId);
    return () => {
      clearCurrentCustomer();
      clearCustomerProfiles();
      clearBodyProfile();
      clearCustomerStats();
    };
  }, [customerId, fetchCustomerDetail, fetchCustomerProfiles, fetchBodyProfile, fetchCustomerStats, clearCurrentCustomer, clearCustomerProfiles, clearBodyProfile, clearCustomerStats]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await Promise.all([
        fetchCustomerDetail(customerId),
        fetchCustomerProfiles(customerId),
        fetchBodyProfile(customerId),
        fetchCustomerStats(customerId),
      ]);
    } finally {
      setRefreshing(false);
    }
  }, [customerId, fetchCustomerDetail, fetchCustomerProfiles, fetchBodyProfile, fetchCustomerStats]);

  const handleDelete = useCallback(async () => {
    setDeleteVisible(false);
    try {
      await deleteCustomer(customerId);
      navigation.goBack();
    } catch (e) {
      Alert.alert(t('common.error'), e.message);
    }
  }, [customerId, deleteCustomer, navigation, t]);

  const handleDeleteProfile = useCallback((profileId, profileName) => {
    Alert.alert(
      t('common.delete'),
      `${t('customer.confirmDelete').replace('customer', '"' + profileName + '"')}`,
      [
        {text: t('common.cancel'), style: 'cancel'},
        {
          text: t('common.delete'),
          style: 'destructive',
          onPress: async () => {
            try {
              await deleteProfile(customerId, profileId);
            } catch (e) {
              Alert.alert(t('common.error'), e.message);
            }
          },
        },
      ],
    );
  }, [customerId, deleteProfile, t]);

  if (isLoading && !currentCustomer) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  const customer = currentCustomer;

  return (
    <View style={styles.container}>
      <Appbar.Header style={styles.appbar} dark>
        <Appbar.BackAction onPress={() => navigation.goBack()} color={colors.white} />
        <Appbar.Content title={customer?.fullName || ''} titleStyle={styles.appbarTitle} />
        <Appbar.Action
          icon="pencil"
          onPress={() => navigation.navigate('CustomerForm', {customerId})}
          color={colors.white}
        />
      </Appbar.Header>

      <ScrollView
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} colors={[colors.primary]} />
        }>
        {customer && (
          <>
            <Card style={styles.infoCard}>
              <Card.Content>
                <View style={styles.avatarRow}>
                  <View style={styles.avatar}>
                    <Text style={styles.avatarText}>
                      {customer.fullName.charAt(0).toUpperCase()}
                    </Text>
                  </View>
                  <View style={styles.nameSection}>
                    <Text variant="headlineSmall" style={styles.name}>{customer.fullName}</Text>
                    {customer.phone && (
                      <View style={styles.phoneRow}>
                        <Text variant="bodyMedium" style={styles.phone}>{customer.phone}</Text>
                        <View style={styles.phoneActions}>
                          <TouchableOpacity
                            onPress={() => Linking.openURL(`tel:${customer.countryCode || ''}${customer.phone}`)}
                            style={styles.phoneActionBtn}>
                            <IconButton icon="phone" size={18} iconColor={colors.success} style={{margin: 0}} />
                          </TouchableOpacity>
                          <TouchableOpacity
                            onPress={() => {
                              const num = (customer.countryCode || '91') + customer.phone.replace(/\D/g, '');
                              Linking.openURL(`whatsapp://send?phone=${num}`);
                            }}
                            style={styles.phoneActionBtn}>
                            <IconButton icon="whatsapp" size={18} iconColor="#25D366" style={{margin: 0}} />
                          </TouchableOpacity>
                        </View>
                      </View>
                    )}
                  </View>
                </View>

                <Divider style={styles.divider} />

                {customer.altPhone && <InfoRow label={t('customer.altPhone')} value={customer.altPhone} />}
                {customer.email && <InfoRow label={t('customer.email')} value={customer.email} />}
                {customer.dateOfBirth && <InfoRow label={t('customer.dateOfBirth')} value={customer.dateOfBirth} />}
                {customer.gender && <InfoRow label={t('customer.gender')} value={customer.gender === 'MALE' ? t('customer.male') : t('customer.female')} />}
                {customer.heightCm && <InfoRow label={t('customer.height')} value={`${customer.heightCm} cm`} />}
                {customer.weightKg && <InfoRow label={t('customer.weight')} value={`${customer.weightKg} kg`} />}
                {customer.referralSourceName && <InfoRow label={t('customer.referralSource')} value={customer.referralSourceName} />}
                {customer.address && <InfoRow label={t('customer.address')} value={customer.address} />}
                {customer.city && <InfoRow label={t('customer.city')} value={customer.city} />}
                {customer.pincode && <InfoRow label={t('customer.pincode')} value={customer.pincode} />}
                {customer.notes && <InfoRow label={t('customer.notes')} value={customer.notes} />}
              </Card.Content>
            </Card>

            {customerStats && (
              <View style={styles.statsGrid}>
                <View style={styles.statsRow}>
                  <TouchableOpacity
                    activeOpacity={0.7}
                    style={{flex: 1}}
                    onPress={() => navigation.navigate('CustomerOrders', {
                      customerId,
                      customerName: customer.fullName,
                      summaryLabel: t('customer.stats.totalRevenue'),
                      summaryAmount: customerStats.totalRevenue || 0,
                      summaryCount: customerStats.orderCount || 0,
                    })}>
                    <Card style={[styles.statsCard, {flex: 1}]}>
                      <Card.Content>
                        <Text variant="bodySmall" style={styles.statsLabel}>
                          {t('customer.stats.totalRevenue')}
                        </Text>
                        <Text variant="titleMedium" style={[styles.statsValue, {color: colors.primary}]}>
                          {'\u20B9'}{Number(customerStats.totalRevenue || 0).toLocaleString('en-IN')}
                        </Text>
                        <Text variant="bodySmall" style={styles.statsSubtext}>
                          {t('customer.stats.orders', {count: customerStats.orderCount || 0})}
                        </Text>
                      </Card.Content>
                    </Card>
                  </TouchableOpacity>
                  <TouchableOpacity
                    activeOpacity={0.7}
                    style={{flex: 1}}
                    onPress={() => navigation.navigate('CustomerDues', {
                      customerId,
                      customerName: customer.fullName,
                    })}>
                    <Card style={[styles.statsCard, {flex: 1}]}>
                      <Card.Content>
                        <Text variant="bodySmall" style={styles.statsLabel}>
                          {t('customer.stats.dueAmount')}
                        </Text>
                        <Text variant="titleMedium" style={[styles.statsValue, {color: customerStats.dueAmount > 0 ? colors.error : colors.success}]}>
                          {'\u20B9'}{Number(customerStats.dueAmount || 0).toLocaleString('en-IN')}
                        </Text>
                        <Text variant="bodySmall" style={styles.statsSubtext}>
                          {t('customer.stats.dueOrders', {count: customerStats.dueOrderCount || 0})}
                        </Text>
                      </Card.Content>
                    </Card>
                  </TouchableOpacity>
                </View>
                <View style={styles.statsRow}>
                  <TouchableOpacity
                    activeOpacity={0.7}
                    style={[styles.statsCardView, {flex: 1}]}
                    onPress={() => navigation.navigate('CustomerLedger', {
                      customerId,
                      customerName: customer.fullName,
                    })}>
                    <Text variant="bodySmall" style={styles.statsLabel}>
                      {t('customer.stats.totalPaid')}
                    </Text>
                    <Text variant="titleMedium" style={[styles.statsValue, {color: colors.success}]}>
                      {'\u20B9'}{Number(customerStats.totalPaid || 0).toLocaleString('en-IN')}
                    </Text>
                    {customerStats.walletBalance > 0 && (
                      <Text variant="bodySmall" style={{color: colors.info, marginTop: 2}}>
                        Credits: {'\u20B9'}{Number(customerStats.walletBalance).toLocaleString('en-IN')}
                      </Text>
                    )}
                  </TouchableOpacity>
                  <TouchableOpacity
                    activeOpacity={0.7}
                    style={[styles.statsCardView, {flex: 1}]}
                    onPress={() => navigation.navigate('CustomerOrders', {
                      customerId,
                      customerName: customer.fullName,
                      summaryLabel: t('customer.stats.recentOrders'),
                      summaryAmount: customerStats.totalRevenue || 0,
                      summaryCount: customerStats.orderCount || 0,
                    })}>
                    <View>
                        <Text variant="bodySmall" style={styles.statsLabel}>
                          {t('customer.stats.recentOrders')}
                        </Text>
                        {customerStats.recentOrders?.length > 0 ? (
                          customerStats.recentOrders.map(order => (
                            <View key={order.id} style={styles.recentOrderRow}>
                              <Text variant="bodySmall" style={styles.recentOrderNumber}>
                                {order.orderNumber}
                              </Text>
                              <Text variant="bodySmall" style={[styles.recentOrderStatus, {color: colors.status[order.status] || colors.textSecondary}]}>
                                {t(`order.status.${order.status}`)}
                              </Text>
                            </View>
                          ))
                        ) : (
                          <Text variant="bodySmall" style={styles.statsSubtext}>
                            {t('customer.stats.noOrders')}
                          </Text>
                        )}
                    </View>
                  </TouchableOpacity>
                </View>
              </View>
            )}

            {(customer.faceImageUrl || customer.bodyImageUrl) && (
              <Card style={styles.infoCard}>
                <Card.Content>
                  <Text variant="titleMedium" style={styles.sectionTitle}>
                    {t('customer.customerPhotos')}
                  </Text>
                  <View style={styles.imageRow}>
                    {customer.faceImageUrl && (
                      <View style={styles.imageContainer}>
                        <Image source={{uri: customer.faceImageUrl}} style={styles.customerImage} />
                        <Text style={styles.imageLabel}>{t('customer.faceImage')}</Text>
                      </View>
                    )}
                    {customer.bodyImageUrl && (
                      <View style={styles.imageContainer}>
                        <Image source={{uri: customer.bodyImageUrl}} style={styles.customerImage} />
                        <Text style={styles.imageLabel}>{t('customer.bodyImage')}</Text>
                      </View>
                    )}
                  </View>
                </Card.Content>
              </Card>
            )}

            <Card style={styles.actionCard}>
              <Card.Content>
                <Text variant="titleMedium" style={styles.sectionTitle}>
                  {t('measurement.bodyMeasurements')}
                </Text>
                {bodyProfile ? (
                  <Card
                    style={styles.profileCard}
                    onPress={() => navigation.navigate('BodyMeasurement', {
                      customerId,
                      customerGender: customer.gender,
                    })}>
                    <Card.Content>
                      <Text variant="bodyMedium" style={styles.profileName}>
                        {bodyProfile.profileName || t('measurement.bodyMeasurements')}
                      </Text>
                      <Text variant="bodySmall" style={styles.profileMeta}>
                        {bodyProfile.measuredAt} &middot; {t('measurement.values', {count: bodyProfile.values?.length || 0})}
                      </Text>
                    </Card.Content>
                  </Card>
                ) : (
                  <Button
                    mode="outlined"
                    icon="human"
                    onPress={() => navigation.navigate('BodyMeasurement', {
                      customerId,
                      customerGender: customer.gender,
                    })}
                    style={styles.actionButton}>
                    {t('measurement.addBodyMeasurement')}
                  </Button>
                )}
              </Card.Content>
            </Card>

            <Card style={styles.actionCard}>
              <Card.Content>
                <Text variant="titleMedium" style={styles.sectionTitle}>
                  {t('measurement.dressMeasurements')}
                </Text>
                {customerProfiles.length > 0 && customerProfiles.filter(p => p.profileType === 'DRESS').map(profile => (
                  <Card
                    key={profile.id}
                    style={styles.profileCard}
                    onPress={() => navigation.navigate('MeasurementForm', {
                      customerId,
                      profileId: profile.id,
                      catalogItemId: profile.catalogItemId,
                    })}>
                    <Card.Content style={styles.profileCardContent}>
                      {profile.catalogItemImageUrl && (
                        <Image
                          source={{uri: profile.catalogItemImageUrl}}
                          style={styles.profileItemImage}
                        />
                      )}
                      <View style={styles.profileCardText}>
                        <Text variant="bodyMedium" style={styles.profileName}>
                          {profile.profileName}
                        </Text>
                        <Text variant="bodySmall" style={styles.profileMeta}>
                          {profile.catalogItemName || ''}{profile.measuredAt ? ` \u00b7 ${profile.measuredAt}` : ''}{profile.values ? ` \u00b7 ${t('measurement.values', {count: profile.values.length})}` : ''}
                        </Text>
                      </View>
                      <IconButton
                        icon="delete-outline"
                        size={20}
                        iconColor={colors.error}
                        onPress={() => handleDeleteProfile(profile.id, profile.profileName)}
                      />
                    </Card.Content>
                  </Card>
                ))}
                <Button
                  mode="outlined"
                  icon="ruler"
                  onPress={() => navigation.navigate('MeasurementForm', {customerId})}
                  style={[styles.actionButton, customerProfiles.length > 0 && {marginTop: spacing.sm}]}>
                  {t('measurement.addDressMeasurement')}
                </Button>
              </Card.Content>
            </Card>

            <Button
              mode="outlined"
              icon="delete"
              textColor={colors.error}
              style={styles.deleteButton}
              onPress={() => setDeleteVisible(true)}>
              {t('customer.deleteCustomer')}
            </Button>
          </>
        )}
      </ScrollView>

      <Portal>
        <Dialog visible={deleteVisible} onDismiss={() => setDeleteVisible(false)} style={{borderRadius: 15}}>
          <Dialog.Title>{t('customer.deleteCustomer')}</Dialog.Title>
          <Dialog.Content>
            <Text>{t('customer.confirmDelete')}</Text>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setDeleteVisible(false)}>{t('common.cancel')}</Button>
            <Button textColor={colors.error} onPress={handleDelete}>{t('common.delete')}</Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  appbar: {backgroundColor: colors.primary},
  appbarTitle: {color: colors.white},
  loadingContainer: {flex: 1, justifyContent: 'center', alignItems: 'center'},
  infoCard: {margin: spacing.md, backgroundColor: colors.surface, borderRadius: 12},
  avatarRow: {flexDirection: 'row', alignItems: 'center', marginBottom: spacing.md},
  avatar: {
    width: 64, height: 64, borderRadius: 32,
    backgroundColor: colors.primary, justifyContent: 'center', alignItems: 'center',
    marginRight: spacing.md,
  },
  avatarText: {color: colors.white, fontSize: 28, fontWeight: 'bold'},
  nameSection: {flex: 1},
  name: {fontWeight: 'bold', color: colors.text},
  phoneRow: {flexDirection: 'row', alignItems: 'center', marginTop: 2},
  phone: {color: colors.textSecondary},
  phoneActions: {flexDirection: 'row', marginLeft: spacing.xs},
  phoneActionBtn: {marginLeft: 0},
  divider: {marginVertical: spacing.md},
  infoRow: {marginBottom: spacing.sm},
  infoLabel: {color: colors.textSecondary, marginBottom: 2},
  infoValue: {color: colors.text},
  actionCard: {marginHorizontal: spacing.md, marginBottom: spacing.md, backgroundColor: colors.surface, borderRadius: 12},
  sectionTitle: {fontWeight: '600', color: colors.text, marginBottom: spacing.sm},
  actionButton: {borderColor: colors.primary},
  profileCard: {backgroundColor: colors.background, borderRadius: 8, marginBottom: spacing.xs},
  profileCardContent: {flexDirection: 'row', alignItems: 'center'},
  profileItemImage: {width: 40, height: 40, borderRadius: 6, backgroundColor: colors.background, marginRight: spacing.sm},
  profileCardText: {flex: 1},
  profileName: {fontWeight: '600', color: colors.text},
  profileMeta: {color: colors.textSecondary, marginTop: 2},
  statsGrid: {marginHorizontal: spacing.md, marginBottom: spacing.md, gap: spacing.sm},
  statsRow: {flexDirection: 'row', gap: spacing.sm, alignItems: 'stretch'},
  statsCard: {backgroundColor: colors.surface, borderRadius: 12, elevation: 0, borderWidth: 1, borderColor: colors.divider},
  statsCardView: {backgroundColor: colors.surface, borderRadius: 12, borderWidth: 1, borderColor: colors.divider, padding: spacing.md},
  statsLabel: {color: colors.textSecondary, marginBottom: spacing.xs},
  statsValue: {fontWeight: 'bold'},
  statsSubtext: {color: colors.textSecondary, marginTop: 2},
  recentOrderRow: {flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 3},
  recentOrderNumber: {color: colors.text, fontSize: 12},
  recentOrderStatus: {fontSize: 11, fontWeight: '600'},
  deleteButton: {margin: spacing.md, borderColor: colors.error},
  imageRow: {flexDirection: 'row', gap: spacing.md},
  imageContainer: {flex: 1, alignItems: 'center'},
  customerImage: {width: '100%', height: 150, borderRadius: 8, backgroundColor: colors.background},
  imageLabel: {fontSize: 12, color: colors.textSecondary, marginTop: spacing.xs, textAlign: 'center'},
});

export default CustomerDetailScreen;
